import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { useState, useEffect } from "react";
import { Checkbox } from '@/shared/components/Checkbox'
import { useNavigate, useSearchParams } from "react-router-dom";
import { estatusDeLaUrl } from "@/features/rh/lib/solicitudesPorEstatus";
import FilterSelect, { type FilterOption } from '@/shared/components/FilterSelect';
import {
  Plus,
  Search,
  Building,
  AlertCircle,
  Eye,
  Link,
  Copy,
  Folder,
} from "lucide-react";
import { seRechazaEnEstado } from '@/features/rh/lib/rechazoDeSolicitudes';
import { apiService, JobRequestStatus } from '@/services/api';
import type { JobRequest } from '@/services/api';
import JobRequestStatusBadge from '@/features/rh/components/job-requests/JobRequestStatusBadge';
import JobRequestActions from '@/features/rh/components/job-requests/JobRequestActions';
import {
  generateCandidateRegistrationLink,
  copyLinkToClipboard,
} from '@/features/rh/lib/candidateLink';
import { formatDateSafe } from '@/shared/lib/dateUtils';
import {
  canAssignJobRequestSalaries,
  canFinalApproveJobRequests,
  canPreApproveJobRequests,
  canRejectJobRequests,
  userHasPermission,
} from '@/shared/lib/roleUtils';
import {
  formatJobRequestArea,
  formatJobRequestContractType,
  formatJobRequestPriority,
  formatJobRequestReason,
  formatJobRequestSede,
} from '@/features/rh/lib/jobRequestDisplay';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { useAuth } from '@/shared/hooks/useAuth';
import { EsqueletoTarjetasTexto } from '@/shared/components/Esqueleto';
import { DatoEnRecuadro } from "@/features/rh/components/DatoEnRecuadro";
import { colorDePrioridad } from "@/features/rh/lib/prioridad";
import BotonDuplicarSolicitud from '@/features/rh/components/job-requests/BotonDuplicarSolicitud';

// Función para limpiar HTML y extraer texto plano
const stripHtml = (html: string): string => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

// Mismas opciones y grupos que tenía el select nativo del navegador.
const STATUS_OPTIONS: FilterOption[] = [
  { value: "pendiente_pre_aprobacion",     label: "Pendiente Pre-aprobación",     group: "Nuevo flujo" },
  { value: "pre_aprobada",                 label: "Pre-aprobada",                 group: "Nuevo flujo" },
  { value: "pendiente_aprobacion_salario", label: "Pendiente Aprobación Salario", group: "Nuevo flujo" },
  { value: "devuelta_rh",                  label: "Devuelta a RH",                group: "Nuevo flujo" },
  { value: "en_revision",                  label: "En revisión",                  group: "Estados generales" },
  { value: "devuelta",                     label: "Devueltas",                    group: "Estados generales" },
  { value: "vacante_activa",               label: "Vacante Activa",               group: "Estados generales" },
  { value: "cubierta",                     label: "Cubierta",                     group: "Estados generales" },
  { value: "rechazada",                    label: "Rechazadas",                   group: "Estados generales" },
  { value: "en_proceso_contratacion",      label: "En Contratación",              group: "Estados generales" },
];

/**
 * Un dato en recuadro de la tarjeta: etiqueta chica arriba, valor abajo.
 *
 * Etiqueta y valor son de tamanos muy distintos —10 y 14— a proposito: la
 * etiqueta esta para ubicar el dato de un vistazo y el valor es lo que se lee.
 */
const JobRequestsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [jobRequests, setJobRequests] = useState<JobRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  // SRH-340: el filtro de estatus vive en la URL (?status=cubierta), que es como
  // llega desde el Inicio de RH. Así el vínculo se comparte, sobrevive a
  // recargar y volver desde un detalle regresa al mismo listado.
  const statusFilter = estatusDeLaUrl(
    searchParams.get("status"),
    STATUS_OPTIONS.map((opcion) => opcion.value),
  );
  const setStatusFilter = (valor: string) => {
    const siguiente = new URLSearchParams(searchParams);
    if (!valor || valor === "all") siguiente.delete("status");
    else siguiente.set("status", valor);
    setSearchParams(siguiente, { replace: true });
  };

  const [showMyRequests, setShowMyRequests] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  // Estos tres filtran del lado del cliente, sobre lo ya cargado. Solo el
  // estatus viaja al servidor, que es como estaba antes.
  const [prioridadFilter, setPrioridadFilter] = useState("");
  const [proyectoFilter, setProyectoFilter] = useState("");
  const [areaFilter, setAreaFilter] = useState("");

  const PRIORIDAD_OPTIONS: FilterOption[] = [
    { value: "urgente", label: "Urgente" },
    { value: "alta", label: "Alta" },
    { value: "media", label: "Media" },
    { value: "baja", label: "Baja" },
  ];

  useEffect(() => {
    loadJobRequests();
  }, [statusFilter, showMyRequests]);

  const loadJobRequests = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const status = statusFilter === "all" ? undefined : statusFilter;
      const data = await apiService.getJobRequests(status, showMyRequests);
      setJobRequests(data);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || "Error al cargar las solicitudes de vacantes");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Las opciones de proyecto y area salen de las solicitudes cargadas, no de
   * sus catalogos: asi la lista no ofrece proyectos donde no hay ninguna
   * solicitud, que es un filtro que solo puede devolver vacio.
   *
   * Lo ya elegido se conserva aunque desaparezca de los datos —pasa al mover
   * el estatus, que si filtra en el servidor— para que el selector no diga
   * «Todos» mientras sigue filtrando.
   */
  const opcionesDeTexto = (
    valores: (string | null | undefined)[],
    elegido: string,
  ): FilterOption[] => {
    const limpios = [
      ...new Set(valores.map((v) => v?.trim()).filter((v): v is string => !!v)),
    ].sort((a, b) => a.localeCompare(b, "es"));

    if (elegido && !limpios.includes(elegido)) limpios.push(elegido);

    return limpios.map((v) => ({ value: v, label: v }));
  };

  const PROYECTO_OPTIONS = opcionesDeTexto(
    jobRequests.map((j) => j.proyecto?.name),
    proyectoFilter,
  );
  const AREA_OPTIONS = opcionesDeTexto(
    jobRequests.map((j) => j.areaVacante),
    areaFilter,
  );

  const hayFiltros =
    searchTerm !== "" ||
    statusFilter !== "all" ||
    prioridadFilter !== "" ||
    proyectoFilter !== "" ||
    areaFilter !== "";

  /**
   * Deja los filtros como al entrar. No toca «Mis solicitudes»: ese no acota
   * la lista, elige de quien es, y apagarlo mostraria las de todos, que es un
   * salto mas grande que limpiar un filtro.
   */
  const limpiarFiltros = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setPrioridadFilter("");
    setProyectoFilter("");
    setAreaFilter("");
  };

  const getStatusBadge = (status: JobRequest["status"]) => {
    // ✨ ACTUALIZADO: 30 Oct 2025 - Usando el nuevo JobRequestStatusBadge
    return <JobRequestStatusBadge status={status} size="sm" />;
  };

  const handleCopyPublicLink = async (
    e: React.MouseEvent,
    jobRequest: JobRequest,
  ) => {
    e.stopPropagation();
    const publicLink = await generateCandidateRegistrationLink(jobRequest.id);
    const success = await copyLinkToClipboard(publicLink);
    if (success) {
      setCopiedLinkId(jobRequest.id.toString());
      setTimeout(() => setCopiedLinkId(null), 2000);
    }
  };

  const filteredJobRequests = jobRequests.filter((jobRequest) => {
    // Filtro de búsqueda por texto
    const matchesSearch =
      (jobRequest.puestoSolicitar &&
        jobRequest.puestoSolicitar
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (jobRequest.areaVacante &&
        jobRequest.areaVacante
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (jobRequest.sede &&
        jobRequest.sede.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (jobRequest.justificacionNecesidad &&
        jobRequest.justificacionNecesidad
          .toLowerCase()
          .includes(searchTerm.toLowerCase()));

    // Si no coincide con la búsqueda, excluir
    if (!matchesSearch) return false;

    if (prioridadFilter && jobRequest.prioridad !== prioridadFilter) return false;
    if (proyectoFilter && jobRequest.proyecto?.name?.trim() !== proyectoFilter)
      return false;
    if (areaFilter && jobRequest.areaVacante?.trim() !== areaFilter) return false;

    // Si plazasCubiertas es true, solo mostrar si se está filtrando por su estatus específico
    if (jobRequest.plazasCubiertas === true) {
      // Si statusFilter es 'all', ocultar
      if (statusFilter === "all") return false;

      // Solo mostrar si el filtro coincide exactamente con el estatus de la card
      return jobRequest.status === statusFilter;
    }

    // Si no tiene plazasCubiertas o es false, mostrar normalmente
    return true;
  });

  const hasVisibleRequestActions = (jobRequest: JobRequest): boolean => {
    const canPreApprove = canPreApproveJobRequests(user || undefined);
    const canAssignSalaries = canAssignJobRequestSalaries(user || undefined);
    const canFinalApprove = canFinalApproveJobRequests(user || undefined);
    const canReject = canRejectJobRequests(user || undefined);
    const canReturnToRH = Boolean(
      user &&
        jobRequest?.solicitante &&
        (user.id === jobRequest.solicitante.id ||
          user.email === jobRequest.solicitante.email),
    );

    const status = jobRequest.status;

    const canShowPreApproveActions =
      canPreApprove &&
      (status === "pendiente_pre_aprobacion" ||
        status === JobRequestStatus.PENDIENTE_VALIDACION_PM ||
        status === JobRequestStatus.DEVUELTA);

    const canShowAssignSalariesAction =
      canAssignSalaries &&
      (status === JobRequestStatus.PRE_APROBADA ||
        status === JobRequestStatus.DEVUELTA_RH ||
        status === JobRequestStatus.EN_REVISION);

    const canShowFinalApproveActions =
      canFinalApprove &&
      (status === "pendiente_aprobacion_salario" ||
        status === JobRequestStatus.EN_REVISION_SALARIOS);

    const canShowReturnToRHAction =
      canReturnToRH &&
      (status === "pendiente_aprobacion_salario" ||
        status === JobRequestStatus.EN_REVISION_SALARIOS);

    // SRH-317: sin esto el bloque ni se monta para una rechazada y el boton
    // Reactivar, que ya vive en JobRequestActions, nunca llega a pintarse.
    const canShowReactivateAction =
      canReject && status === JobRequestStatus.RECHAZADA;

    // Rechazar lo habilita el permiso «Eliminar solicitudes», no el de aprobar.
    const canShowRejectAction = canReject && seRechazaEnEstado(status);

    return (
      canShowRejectAction ||
      canShowPreApproveActions ||
      canShowAssignSalariesAction ||
      canShowFinalApproveActions ||
      canShowReturnToRHAction ||
      canShowReactivateAction
    );
  };

  // A-01: la silueta conserva la forma del listado mientras cargan las solicitudes.
  if (isLoading) {
    return (
      // La silueta imita las tarjetas del listado, que no es una tabla.
      <EsqueletoTarjetasTexto cuantas={6} />
    );
  }

  if (
    userHasPermission(
      "rh.solicitudes_personal.base.read",
      user?.rbac?.effectivePermissions || [],
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6 mt-2 mb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <TituloDePantalla className="mb-2">
            Mis solicitudes de personal
          </TituloDePantalla>
          <p className="text-gray-600">
            Administra aquí tu lista de solicitudes de personal
          </p>
        </div>
        {userHasPermission(
          "rh.solicitudes_personal.base.create",
          user?.rbac?.effectivePermissions || [],
        ) && (
          <button
            onClick={() => navigate(`/job-requests/new`)}
            className={estiloDeBoton('primario')}
          >
            <Plus className="w-4 h-4" />
            Nueva solicitud
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-[#E0E0E0] p-5">
        {/*
          Fila que se acomoda sola: la busqueda se estira y los cuatro
          selectores miden lo suyo. Con rejilla de columnas iguales, «Prioridad»
          quedaba igual de ancho que «Proyecto», que es el que lleva los textos
          largos.
        */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar solicitudes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
            />
          </div>

          <FilterSelect
            /* 130 y no 150: el texto mas largo que llega a mostrar es
               «Prioridad», que con su padding y el chevron pide 123px. */
            className="w-[130px]"
            label="Prioridad"
            allLabel="Toda prioridad"
            options={PRIORIDAD_OPTIONS}
            value={prioridadFilter}
            onChange={setPrioridadFilter}
          />

          <FilterSelect
            className="w-[190px]"
            label="Estatus"
            allLabel="Todos los estados"
            options={STATUS_OPTIONS}
            value={statusFilter === "all" ? "" : statusFilter}
            onChange={(v) => setStatusFilter(v || "all")}
          />

          <FilterSelect
            className="w-[170px]"
            label="Proyecto"
            allLabel="Todos los proyectos"
            options={PROYECTO_OPTIONS}
            value={proyectoFilter}
            onChange={setProyectoFilter}
          />

          <FilterSelect
            className="w-[170px]"
            label="Área"
            allLabel="Todas las áreas"
            options={AREA_OPTIONS}
            value={areaFilter}
            onChange={setAreaFilter}
          />

          {/* Enlace de texto y no botón con borde, como en el diseño. Siempre
              visible, apagado cuando no hay nada que limpiar: que aparezca y
              desaparezca movería los demás filtros de lugar justo cuando se
              están usando.

              El azul es el de la pastilla de estatus del mismo diseño; el
              panel de Figma no alcanza a mostrar el del enlace. */}
          <button
            type="button"
            onClick={limpiarFiltros}
            disabled={!hayFiltros}
            title="Limpiar filtros"
            className="px-1 text-xs font-bold underline underline-offset-2 text-[#3662E3] transition-colors hover:text-[#2748B8] disabled:cursor-default disabled:text-[#B5B5B5] disabled:no-underline"
          >
            Limpiar
          </button>

          <label className="flex items-center">
            <Checkbox
              checked={showMyRequests}
              onChange={(e) => setShowMyRequests(e.target.checked)}
              className="flex-shrink-0"
            />
            <span className="ml-2 text-sm text-gray-700 whitespace-nowrap">
              Mis solicitudes
            </span>
          </label>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Job Requests Grid */}
      {filteredJobRequests.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-heading-sm text-secondary mb-2">
            No hay solicitudes de vacantes
          </h3>
          {/* `hayFiltros` en vez de repetir la lista de filtros: al agregar
              prioridad, proyecto y area, esta condicion se queda atras y la
              pantalla acaba diciendo que no hay solicitudes cuando lo que pasa
              es que el filtro no deja pasar ninguna. */}
          <p className="text-gray-600 mb-4">
            {hayFiltros || showMyRequests
              ? "No se encontraron solicitudes con los filtros aplicados."
              : "Aún no se han creado solicitudes de vacantes."}
          </p>
          {!hayFiltros &&
            !showMyRequests &&
            userHasPermission(
              "rh.solicitudes_personal.base.create",
              user?.rbac?.effectivePermissions || [],
            ) && (
              <button
                onClick={() => navigate(`/job-requests/new`)}
                className={estiloDeBoton('primario')}
              >
                Crear Primera Solicitud
              </button>
            )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {filteredJobRequests.map((jobRequest) => (
            (() => {
              const showActionsSection = hasVisibleRequestActions(jobRequest);
              return (
            <div
              key={jobRequest.id}
              className="flex flex-col rounded-xl border border-[#E5E5E5] bg-white p-5 font-['Albert_Sans'] shadow-sm transition-shadow hover:shadow-md"
            >
              {/* Estatus, prioridad y el enlace publico */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  {getStatusBadge(jobRequest.status)}
                  {jobRequest.prioridad && (
                    <span
                      className="inline-flex items-center gap-1 text-xs font-medium"
                      style={{ color: colorDePrioridad(jobRequest.prioridad) }}
                    >
                      <span aria-hidden>&bull;</span>
                      Prioridad {formatJobRequestPriority(jobRequest.prioridad).toLowerCase()}
                    </span>
                  )}
                </div>

                {jobRequest.status === JobRequestStatus.APROBADA && (
                  <button
                    onClick={(e) => handleCopyPublicLink(e, jobRequest)}
                    className="-mr-1 -mt-1 flex-shrink-0 rounded-lg p-1.5 text-[#3662E3] transition-colors hover:bg-[#EEF2FD]"
                    title="Copiar link público para candidatos"
                  >
                    {copiedLinkId === jobRequest.id.toString() ? (
                      <Copy className="h-5 w-5 text-green-500" />
                    ) : (
                      <Link className="h-5 w-5" />
                    )}
                  </button>
                )}
              </div>

              {/* Puesto, proyecto y sede. Todo el bloque lleva al detalle. */}
              <div
                className="flex-1 cursor-pointer"
                onClick={() => navigate(`/job-requests/${jobRequest.id}`)}
              >
                <h3 className="mt-3 text-[20px] font-semibold leading-tight text-[#111111]">
                  {jobRequest.puestoSolicitar || "Sin título"}
                </h3>

                {/* El proyecto solo sale cuando lo hay: la mayoria de las
                    solicitudes no son por proyecto y un icono suelto sin texto
                    se lee como un dato que falta. */}
                {/* `min-w-0` y `max-w-full` no son adorno: sin ellos estos
                    spans son hijos flex con `min-width: auto`, se niegan a
                    encogerse por debajo del largo del texto y una sede larga se
                    sale de la tarjeta. El icono va con `items-start` para que
                    quede a la altura del primer renglon cuando el texto cae a
                    dos. */}
                {(jobRequest.proyecto?.name || jobRequest.sede) && (
                  <div className="mt-2 flex flex-wrap items-start gap-x-6 gap-y-1 text-sm text-[#787878]">
                    {jobRequest.proyecto?.name && (
                      <span className="inline-flex min-w-0 max-w-full items-start gap-2">
                        <Folder className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <span className="min-w-0 break-words">
                          {jobRequest.proyecto.name}
                        </span>
                      </span>
                    )}
                    {jobRequest.sede && (
                      <span className="inline-flex min-w-0 max-w-full items-start gap-2">
                        <Building className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <span className="min-w-0 break-words">
                          {formatJobRequestSede(jobRequest.sede)}
                        </span>
                      </span>
                    )}
                  </div>
                )}

                {/* Inicio, vacantes y contrato */}
                <div className="mt-4 flex flex-wrap gap-2">
                  <DatoEnRecuadro
                    etiqueta="Inicio"
                    valor={formatDateSafe(
                      jobRequest.fechaRequeridaInicio || jobRequest.fechaCreacion,
                      "short",
                    )}
                  />
                  <DatoEnRecuadro
                    etiqueta="Vacantes"
                    valor={String(jobRequest.numeroPlazas || 1)}
                  />
                  {jobRequest.tipoContrato && (
                    <DatoEnRecuadro
                      etiqueta="Contrato"
                      valor={formatJobRequestContractType(jobRequest.tipoContrato)}
                    />
                  )}
                </div>

                <dl className="mt-4 space-y-1.5 text-sm">
                  <div className="flex gap-2">
                    <dt className="text-[#6A6B6C]">Área:</dt>
                    <dd className="min-w-0 truncate text-[#111111]">
                      {formatJobRequestArea(jobRequest.areaVacante)}
                    </dd>
                  </div>
                  {jobRequest.motivo && (
                    <div className="flex gap-2">
                      <dt className="text-[#6A6B6C]">Motivo:</dt>
                      <dd className="min-w-0 truncate text-[#111111]">
                        {formatJobRequestReason(jobRequest.motivo)}
                      </dd>
                    </div>
                  )}
                </dl>

                <div className="mt-4 border-t border-[#F4F4F4] pt-4">
                  <p className="text-sm text-[#6A6B6C]">Justificación:</p>
                  <p className="mt-1 line-clamp-2 text-sm text-[#111111]">
                    {stripHtml(
                      jobRequest.justificacionNecesidad ||
                        jobRequest.habilidadesMinimas ||
                        "Sin descripción",
                    )}
                  </p>
                </div>
              </div>

              {/* Pie: cuando se pidio, su folio y la entrada al detalle. El
                  bloque de arriba lleva flex-1, asi que el pie queda pegado
                  abajo y las tarjetas de un mismo renglon terminan alineadas
                  aunque la justificacion mida distinto. */}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#F4F4F4] pt-4">
                <p className="text-xs">
                  <span className="text-[#787878]">Solicitada: </span>
                  <span className="text-[#121212]">
                    {formatDateSafe(jobRequest.fechaCreacion, "short")}
                  </span>
                  <span className="ml-3 text-[#787878]">ID: </span>
                  <span className="text-[#121212]">{jobRequest.id}</span>
                </p>

                <div className="flex items-start gap-2">
                  <BotonDuplicarSolicitud idSolicitud={jobRequest.id} />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/job-requests/${jobRequest.id}`);
                    }}
                    className={estiloDeBoton('secundario')}
                  >
                    <Eye className="h-4 w-4" />
                    Ver detalle
                  </button>
                </div>
              </div>

              {/* Las acciones del flujo, en su propio renglon: el diseno solo
                  contempla Ver detalle, pero quitarlas dejaria pre-aprobar,
                  asignar salarios o devolver a RH a dos clics de distancia. */}
              {showActionsSection && (
                <div className="mt-4 border-t border-[#F4F4F4] pt-4">
                  <JobRequestActions
                    jobRequest={jobRequest}
                    onUpdate={loadJobRequests}
                  />
                </div>
              )}
            </div>
              );
            })()
          ))}
        </div>
      )}
    </div>
  );
};

export default JobRequestsList;
